// FloorPlanGenerator3D.js
import React, { useState, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Grid, Text } from '@react-three/drei';

const Room3D = ({ position, size, color, label, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const meshRef = useRef();

  const wallHeight = 2.5;
  
  return (
    <group position={[position[0], wallHeight / 2, position[1]]}>
      <mesh
        ref={meshRef}
        onClick={onClick}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <boxGeometry args={[size[0], wallHeight, size[1]]} />
        <meshStandardMaterial 
          color={color} 
          transparent 
          opacity={0.8}
          wireframe={hovered}
        />
      </mesh>
      
      <mesh position={[0, -wallHeight/2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[size[0], size[1]]} />
        <meshStandardMaterial color={color} />
      </mesh>

      <Text
        position={[0, wallHeight/2 + 0.1, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        fontSize={0.5}
        color="black"
      >
        {label}
      </Text>
    </group>
  );
};

const CameraController = () => {
  const { camera } = useThree();
  useFrame(() => {
    camera.lookAt(0, 0, 0);
  });
  return <OrbitControls enableDamping makeDefault enableZoom={true} />;
};

const FloorPlanGenerator3D = () => {
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const gridSize = 2;

  const roomTypes = [
    { name: 'Residential', color: '#FFE4B5' },
    { name: 'Commercial', color: '#E6E6FA' },
    { name: 'Parks', color: '#98FB98' },
    { name: 'School', color: '#87CEEB' },
    { name: 'Hospital', color: '#DDA0DD' },
    { name: 'Industry', color: '#F0E68C' },
    { name: 'Parking', color: '#D3D3D3' },
  ];

  const handlePlaceRoom = (e) => {
    if (!currentRoom || !e.point) return;
    
    const x = Math.round(e.point.x / gridSize) * gridSize;
    const z = Math.round(e.point.z / gridSize) * gridSize;
    
    setRooms([...rooms, {
      id: Date.now(),
      type: currentRoom,
      position: [x, z],
      size: [gridSize * 2, gridSize * 2],
      color: roomTypes.find(r => r.name === currentRoom).color
    }]);
  };

  const handleDelete = (id) => {
    setRooms(rooms.filter(room => room.id !== id));
  };

  const handleSave = () => {
    localStorage.setItem('floorPlan3D', JSON.stringify(rooms));
    alert('Township plan saved!');
  };

  const handleLoad = () => {
    const savedPlan = localStorage.getItem('floorPlan3D');
    if (savedPlan) {
      setRooms(JSON.parse(savedPlan));
      alert('Township plan loaded!');
    } else {
      alert('No saved township plan found.');
    }
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear the township plan?')) {
      setRooms([]);
    }
  };

  return (
    <div className="floor-plan-container">
      <div className="toolbar">
        <div className="room-buttons">
          {roomTypes.map((type) => (
            <button
              key={type.name}
              onClick={() => setCurrentRoom(type.name)}
              className={`room-button ${currentRoom === type.name ? 'active' : ''}`}
              style={{ backgroundColor: currentRoom === type.name ? type.color : '' }}
            >
              {type.name}
            </button>
          ))}
        </div>
        <div className="action-buttons">
          <button onClick={handleSave} className="action-button">Save</button>
          <button onClick={handleLoad} className="action-button">Load</button>
          <button onClick={handleClear} className="action-button clear">Clear</button>
        </div>
      </div>

      <div style={{ height: '600px', width: '100%' }}>
        <Canvas
          camera={{ position: [10, 10, 10], fov: 60 }}
          shadows
        >
          <CameraController />
          <ambientLight intensity={0.5} />
          <directionalLight position={[10, 10, 5]} intensity={1} castShadow />
          
          <Grid
            args={[100, 100]}
            position={[0, 0, 0]}
            cellSize={gridSize}
            cellThickness={0.5}
            cellColor="#6f6f6f"
            sectionSize={5}
          />

          {rooms.map((room) => (
            <Room3D
              key={room.id}
              position={room.position}
              size={room.size}
              color={room.color}
              label={room.type}
              onClick={() => handleDelete(room.id)}
            />
          ))}

          <mesh 
            rotation={[-Math.PI / 2, 0, 0]} 
            position={[0, 0, 0]}
            onClick={handlePlaceRoom}
          >
            <planeGeometry args={[100, 100]} />
            <meshStandardMaterial visible={false} />
          </mesh>
        </Canvas>
      </div>

      <div className="instructions">
        <h3>Instructions:</h3>
        <ol>
          <li>Select a zone type from the buttons above</li>
          <li>Click on the grid to place a zone</li>
          <li>Use mouse to orbit around the scene (left-click + drag)</li>
          <li>Zoom in/out with mouse wheel</li>
          <li>Click on a zone to delete it</li>
          <li>Use Save/Load to preserve your work</li>
        </ol>
      </div>
    </div>
  );
};

export default FloorPlanGenerator3D;